import type { OrderingMenuItem, OrderingMenuSection } from '@/domains/pjazza/types/models/OrderingMenu.types.ts'

/** Letters with no accent mark to strip: Maltese ħ and Croatian đ. See DEC-2026-120. */
const LETTER_FOLDS: Record<string, string> = { ħ: 'h', đ: 'd' }

/** Where a dish matched, best first. */
const MATCH_RANK = { NAME: 0, DESCRIPTION: 1, SECTION: 2 } as const

type MatchRank = (typeof MATCH_RANK)[keyof typeof MATCH_RANK]

interface IndexedDish {
  item: OrderingMenuItem
  name: string
  description: string
}

interface IndexedSection {
  section: OrderingMenuSection
  name: string
  dishes: IndexedDish[]
}

/** A menu with every searchable text folded once, so a key press only compares strings. */
type MenuIndex = readonly IndexedSection[]

/** Lower case, accents off, ħ and đ folded, so "hobz" meets "Ħobż" and "cafe" meets "Café". */
const foldForSearch = (text: string): string =>
  text
    .toLocaleLowerCase()
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .replace(/[ħđ]/g, letter => LETTER_FOLDS[letter])

const indexMenu = (sections: readonly OrderingMenuSection[]): MenuIndex =>
  sections.map(section => ({
    section,
    name: foldForSearch(section.name),
    dishes: section.items.map(item => ({
      item,
      name: foldForSearch(item.name),
      description: foldForSearch(item.description ?? '')
    }))
  }))

const rankOf = (dish: IndexedDish, sectionMatches: boolean, query: string): MatchRank | null => {
  if (dish.name.includes(query)) return MATCH_RANK.NAME
  if (dish.description.includes(query)) return MATCH_RANK.DESCRIPTION
  if (sectionMatches) return MATCH_RANK.SECTION

  return null
}

/**
 * The menu cut down to what matches, still grouped by section.
 *
 * Name matches rank above description matches, and both above a section whose
 * own name matched. Sections follow their best match, then menu order; dishes
 * follow their rank, then menu order. A blank query returns the menu whole.
 */
const searchMenu = (index: MenuIndex, rawQuery: string): OrderingMenuSection[] => {
  const query = foldForSearch(rawQuery.trim())

  if (query === '') return index.map(entry => entry.section)

  return index
    .map(entry => {
      const sectionMatches = entry.name.includes(query)

      const ranked = entry.dishes
        .map(dish => ({ item: dish.item, rank: rankOf(dish, sectionMatches, query) }))
        .filter((match): match is { item: OrderingMenuItem; rank: MatchRank } => match.rank !== null)
        .sort((a, b) => a.rank - b.rank)

      return { section: { ...entry.section, items: ranked.map(match => match.item) }, best: ranked[0]?.rank }
    })
    .filter(result => result.best !== undefined)
    .sort((a, b) => (a.best ?? 0) - (b.best ?? 0))
    .map(result => result.section)
}

export type { MenuIndex }
export { foldForSearch, indexMenu, searchMenu }
